import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@clerk/clerk-react";
import ImagePickerModal from "../../../../../components/popups/imagePicker/ImagePickerModal";
import FeaturePaywallModal from "../../../../../components/popups/paywall/FeaturePaywallModal";
import { useImagePickerModal } from "../../../../../hooks/app/useImagePickerModal";
import {
  fetchCreationImageBlob,
  fetchExportImageBlob,
  fetchUploadImageBlob,
} from "../../../../../services/api/editor/background_removal";
import {
  libraryItemCreationId,
  libraryItemExportId,
} from "../../../../../utils/openLibraryInEditor";
import { ensureBackgroundRemoved } from "../../../../../utils/ensureBackgroundRemoved";
import { canAccessPaywallFeature } from "../../../../../data/paywallFeatures";
import ToolPrepUploadDropzone from "../../../shared/Popup/ToolPrepUploadDropzone";
import Tooltip from "../../../../../components/common/Tooltip/Tooltip";
import { runWithMinUploadProgress } from "../../../../../utils/runWithMinUploadProgress";
import {
  getWatermarkFontStack,
  getWatermarkFontsForType,
  WATERMARK_CONTENT_TYPES,
} from "../../../watermark/Popup/watermarkSettings";
import SignPdfUploadMark, {
  beginSignPdfUploadCrop,
} from "./SignPdfUploadMark";
import SignPdfDrawPad from "./SignPdfDrawPad";
import {
  applySignPdfMode,
  canApplySignPdfDraft,
  DEFAULT_SIGN_PDF_CANVAS_THEME_ID,
  getSignPdfCanvasTheme,
  hasSignPdfSignature,
  SIGN_PDF_CANVAS_THEMES,
  SIGN_PDF_MARK_KINDS,
  SIGN_PDF_MODE_OPTIONS,
  SIGN_PDF_MODES,
  signPdfModeFromSettings,
  withDefaultSignPdfCanvas,
  withSignPdfSingleMark,
} from "./signPdfSettings";
import "./SignPdfSignatureModal.css";

const MARK_COPY = {
  [SIGN_PDF_MARK_KINDS.SIGNATURE]: {
    title: "Add your signature",
    subtitle:
      "Choose a method on the left, craft it on the right, then place it on your PDF.",
    railLabel: "Signature method",
    apply: "Use signature",
    uploadName: "signature.png",
    nameLabel: "Signature name",
    fontLabel: "Signature font",
    placeholder: "Your signature",
  },
  [SIGN_PDF_MARK_KINDS.INITIALS]: {
    title: "Add your initials",
    subtitle:
      "Choose a method on the left, craft initials on the right, then place them on your PDF.",
    railLabel: "Initials method",
    apply: "Use initials",
    uploadName: "initials.png",
    nameLabel: "Initials",
    fontLabel: "Initials font",
    placeholder: "Your initials",
  },
};

const CLEARED_SIGNATURE_SOURCE = {
  logoFile: null,
  logoCreationId: null,
  logoRootCreationId: null,
  logoOriginalUrl: null,
  logoBgRemoved: false,
};

function fileFromBlob(blob, filename = "signature.png") {
  if (blob instanceof File) return blob;
  const type = blob?.type || "image/png";
  const name = String(filename || "signature.png").replace(/[^\w.\-]+/g, "_");
  return new File([blob], name || "signature.png", { type });
}

const TypeIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path
      d="M5 18c2.5-6 4-10 7-10s4.5 4 7 10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M8.5 14.5h7"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path
      d="M12 16V5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M8 8.5 12 4.5 16 8.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5 19h14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const DrawIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path
      d="M5 17.5c2.2-1.2 3.6-3.8 5.2-5.6 1.8-2 4.2-3.2 6.6-2.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M15.5 7.5 18 5l1.5 1.5-2.5 2.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MODE_ICONS = {
  [SIGN_PDF_MODES.TYPE]: TypeIcon,
  [SIGN_PDF_MODES.UPLOAD]: UploadIcon,
  [SIGN_PDF_MODES.DRAW]: DrawIcon,
};

const CanvasThemeSwitcher = ({ canvasTheme, onSelect }) => (
  <div
    className="spsm-canvas-themes"
    role="group"
    aria-label="Canvas background"
  >
    {SIGN_PDF_CANVAS_THEMES.map((theme) => (
      <button
        key={theme.id}
        type="button"
        className={`spsm-canvas-theme${
          canvasTheme.id === theme.id ? " is-selected" : ""
        }`}
        style={{ backgroundColor: theme.textColor }}
        title={theme.label}
        aria-label={theme.label}
        aria-pressed={canvasTheme.id === theme.id}
        onClick={() => onSelect(theme.id)}
      />
    ))}
  </div>
);

/**
 * Signature / initials creator — left mode rail + right editing pane.
 */
const SignPdfSignatureModal = ({
  open = false,
  kind = SIGN_PDF_MARK_KINDS.SIGNATURE,
  initialSettings = null,
  onClose = () => {},
  onApply = () => {},
}) => {
  const titleId = useId();
  const copy = MARK_COPY[kind] || MARK_COPY[SIGN_PDF_MARK_KINDS.SIGNATURE];
  const [draft, setDraft] = useState(initialSettings);
  const [fontOpen, setFontOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [bgRemoveBusy, setBgRemoveBusy] = useState(false);
  const [bgRemoveError, setBgRemoveError] = useState(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [imageSelected, setImageSelected] = useState(false);
  const draftRef = useRef(draft);
  const logoObjectUrlRef = useRef(null);
  const cutoutUrlRef = useRef(null);
  const fontRef = useRef(null);
  draftRef.current = draft;

  const { openImagePicker, imagePickerProps } = useImagePickerModal();
  const { getToken } = useAuth();

  useEffect(() => {
    if (!open) return;
    // New marks always open on the default black canvas.
    const seed = hasSignPdfSignature(initialSettings)
      ? initialSettings
      : withDefaultSignPdfCanvas(initialSettings);
    setDraft(seed);
    setFontOpen(false);
    setUploadProgress(null);
    setBgRemoveBusy(false);
    setBgRemoveError(null);
    setPaywallOpen(false);
    setImageSelected(Boolean(seed?.logoUrl));
    // Only hydrate when this modal opens — not when nested pickers open/close.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !imagePickerProps?.isOpen && !paywallOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, imagePickerProps?.isOpen, paywallOpen]);

  useEffect(() => {
    return () => {
      if (logoObjectUrlRef.current?.startsWith("blob:")) {
        URL.revokeObjectURL(logoObjectUrlRef.current);
      }
      if (cutoutUrlRef.current?.startsWith("blob:")) {
        URL.revokeObjectURL(cutoutUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!fontOpen) return undefined;
    const onDoc = (e) => {
      if (fontRef.current && !fontRef.current.contains(e.target)) {
        setFontOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [fontOpen]);

  const mode = signPdfModeFromSettings(draft);
  const fonts = useMemo(
    () => getWatermarkFontsForType(WATERMARK_CONTENT_TYPES.SIGNATURE),
    []
  );
  const font = fonts.find((f) => f.id === draft?.fontId) || fonts[0] || null;
  const canApply = canApplySignPdfDraft(draft);
  const canvasTheme = getSignPdfCanvasTheme(
    draft?.signPdfCanvasTheme || DEFAULT_SIGN_PDF_CANVAS_THEME_ID
  );

  const patch = (partial) => {
    setDraft((prev) => ({ ...prev, ...partial }));
  };

  const selectCanvasTheme = (themeId) => {
    const theme = getSignPdfCanvasTheme(themeId);
    patch({
      signPdfCanvasTheme: theme.id,
      color: theme.textColor,
    });
  };

  const selectMode = (nextMode) => {
    setFontOpen(false);
    setBgRemoveError(null);
    setDraft((prev) => {
      const next = applySignPdfMode(prev, nextMode);
      if (
        nextMode === SIGN_PDF_MODES.DRAW ||
        nextMode === SIGN_PDF_MODES.UPLOAD
      ) {
        return { ...next, text: "" };
      }
      return next;
    });
  };

  const revokeLogoUrl = () => {
    if (logoObjectUrlRef.current?.startsWith("blob:")) {
      URL.revokeObjectURL(logoObjectUrlRef.current);
    }
    logoObjectUrlRef.current = null;
  };

  const applySignatureFromUrl = useCallback((url, source = {}) => {
    if (!url) return;
    setBgRemoveError(null);
    const apply = (nw, nh) => {
      setDraft((prev) =>
        withSignPdfSingleMark({
          ...prev,
          signPdfMode: source.signPdfMode || SIGN_PDF_MODES.UPLOAD,
          contentType: WATERMARK_CONTENT_TYPES.LOGO,
          logoUrl: url,
          logoOriginalUrl: url,
          logoNaturalWidth: nw,
          logoNaturalHeight: nh,
          logoBgRemoved: false,
          logoFile: source.logoFile instanceof File ? source.logoFile : null,
          logoCreationId: source.logoCreationId ?? null,
          logoRootCreationId: source.logoRootCreationId ?? null,
          logoCropAspect: "custom",
          logoCropRect: null,
          logoCropFrame: null,
          logoCropImageFrame: null,
          logoCropActive: false,
          logoCropSessionStartImageFrame: null,
          logoCropInset: 0,
        })
      );
      setImageSelected(true);
    };
    const img = new Image();
    if (
      typeof url === "string" &&
      /^https?:\/\//i.test(url) &&
      !url.startsWith("blob:")
    ) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => {
      apply(img.naturalWidth || img.width || 0, img.naturalHeight || img.height || 0);
    };
    img.onerror = () => apply(0, 0);
    img.src = url;
  }, []);

  const applyDrawnSignature = useCallback((payload) => {
    if (!payload?.url) {
      setDraft((prev) => {
        if (prev?.signPdfMode !== SIGN_PDF_MODES.DRAW) return prev;
        if (logoObjectUrlRef.current?.startsWith("blob:")) {
          URL.revokeObjectURL(logoObjectUrlRef.current);
          logoObjectUrlRef.current = null;
        }
        return withSignPdfSingleMark({
          ...prev,
          signPdfMode: SIGN_PDF_MODES.DRAW,
          contentType: WATERMARK_CONTENT_TYPES.LOGO,
          ...CLEARED_SIGNATURE_SOURCE,
          logoUrl: null,
          logoNaturalWidth: 0,
          logoNaturalHeight: 0,
          logoCropAspect: "custom",
          logoCropRect: null,
          logoCropFrame: null,
          logoCropImageFrame: null,
          logoCropActive: false,
          logoCropSessionStartImageFrame: null,
          logoCropInset: 0,
        });
      });
      setImageSelected(false);
      return;
    }

    if (
      logoObjectUrlRef.current?.startsWith("blob:") &&
      logoObjectUrlRef.current !== payload.url
    ) {
      URL.revokeObjectURL(logoObjectUrlRef.current);
    }
    logoObjectUrlRef.current = payload.url;

    setBgRemoveError(null);
    setDraft((prev) =>
      withSignPdfSingleMark({
        ...prev,
        signPdfMode: SIGN_PDF_MODES.DRAW,
        contentType: WATERMARK_CONTENT_TYPES.LOGO,
        // Clear typed text so preview/bake treat this as an image mark.
        text: "",
        logoUrl: payload.url,
        logoOriginalUrl: payload.url,
        logoNaturalWidth: payload.width || 0,
        logoNaturalHeight: payload.height || 0,
        logoBgRemoved: false,
        logoFile: payload.file instanceof File ? payload.file : null,
        logoCreationId: null,
        logoRootCreationId: null,
        logoCropAspect: "custom",
        logoCropRect: null,
        logoCropFrame: null,
        logoCropImageFrame: null,
        logoCropActive: false,
        logoCropSessionStartImageFrame: null,
        logoCropInset: 0,
      })
    );
    setImageSelected(false);
  }, []);

  const applyLibrarySignature = useCallback(
    async (item) => {
      if (!item) return;
      const exportId = libraryItemExportId(item);
      const creationId = libraryItemCreationId(item);
      const rootCreationId =
        item.kind === "upload"
          ? item.root_creation_id ?? item.creation_id ?? creationId
          : item.root_creation_id ?? null;
      const source = {
        logoCreationId: creationId,
        logoRootCreationId: rootCreationId,
      };

      try {
        let blob = null;
        let outputUrlFallback = null;

        await runWithMinUploadProgress({
          setProgress: setUploadProgress,
          minMs: 1400,
          work: async () => {
            const token = await getToken();
            if (!token) throw new Error("Sign in required.");

            if (item.kind === "upload") {
              const uploadId = source.logoRootCreationId ?? creationId;
              if (!uploadId) throw new Error("Could not resolve library image.");
              blob = await fetchUploadImageBlob(token, uploadId);
            } else if (item.kind === "export" || exportId) {
              blob = await fetchExportImageBlob(token, exportId);
            } else if (creationId) {
              blob = await fetchCreationImageBlob(token, creationId);
            } else if (item.output_url) {
              outputUrlFallback = item.output_url;
              return;
            } else {
              throw new Error("Could not resolve library image.");
            }

            if (!blob || blob.type?.includes("json")) {
              throw new Error("Could not load library image.");
            }
          },
          onReady: () => {
            if (outputUrlFallback) {
              revokeLogoUrl();
              logoObjectUrlRef.current = null;
              applySignatureFromUrl(outputUrlFallback, source);
              return;
            }
            const file = fileFromBlob(
              blob,
              item.file_name || item.name || copy.uploadName
            );
            revokeLogoUrl();
            const url = URL.createObjectURL(file);
            logoObjectUrlRef.current = url;
            applySignatureFromUrl(url, { ...source, logoFile: file });
          },
        });
      } catch (err) {
        console.error("[SignPdfSignatureModal] library pick failed:", err);
        setUploadProgress(null);
        if (item.output_url && !logoObjectUrlRef.current) {
          revokeLogoUrl();
          applySignatureFromUrl(item.output_url, source);
        }
      }
    },
    [applySignatureFromUrl, getToken]
  );

  const openSignaturePicker = () => {
    if (uploadProgress != null || bgRemoveBusy) return;
    openImagePicker({
      onPickFile: async (file) => {
        if (!file) return;
        try {
          await runWithMinUploadProgress({
            setProgress: setUploadProgress,
            onReady: () => {
              revokeLogoUrl();
              const url = URL.createObjectURL(file);
              logoObjectUrlRef.current = url;
              applySignatureFromUrl(url, { logoFile: file });
            },
          });
        } catch (err) {
          console.error("[SignPdfSignatureModal] pick failed:", err);
          setUploadProgress(null);
        }
      },
      onPickLibrary: applyLibrarySignature,
    });
  };

  const clearSignatureImage = () => {
    revokeLogoUrl();
    setBgRemoveError(null);
    setImageSelected(false);
    patch({
      logoUrl: null,
      logoNaturalWidth: 0,
      logoNaturalHeight: 0,
      ...CLEARED_SIGNATURE_SOURCE,
      logoCropAspect: "custom",
      logoCropRect: null,
      logoCropFrame: null,
      logoCropImageFrame: null,
      logoCropActive: false,
      logoCropSessionStartImageFrame: null,
      logoCropInset: 0,
    });
  };

  const applyCutoutLogo = (url, cutoutCreationId = null) => {
    const apply = (nw, nh) => {
      setDraft((prev) => ({
        ...prev,
        logoUrl: url,
        logoOriginalUrl: prev.logoOriginalUrl || prev.logoUrl,
        logoNaturalWidth: nw,
        logoNaturalHeight: nh,
        logoBgRemoved: true,
        logoCreationId: cutoutCreationId ?? prev.logoCreationId,
        logoCropAspect: "custom",
        logoCropRect: null,
        logoCropFrame: null,
        logoCropImageFrame: null,
        logoCropActive: false,
        logoCropSessionStartRect: null,
        logoCropSessionStartImageFrame: null,
        logoCropInset: 0,
      }));
    };
    const img = new Image();
    if (
      typeof url === "string" &&
      /^https?:\/\//i.test(url) &&
      !url.startsWith("blob:")
    ) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => {
      apply(img.naturalWidth || img.width || 0, img.naturalHeight || img.height || 0);
    };
    img.onerror = () => apply(0, 0);
    img.src = url;
  };

  const runBgRemove = async () => {
    const base = draftRef.current;
    if (bgRemoveBusy || base?.logoBgRemoved || !base?.logoUrl) return;
    setBgRemoveBusy(true);
    setBgRemoveError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error("Sign in to remove the background.");
      const { cutoutUrl, creationId } = await ensureBackgroundRemoved({
        token,
        imageFile: base.logoFile instanceof File ? base.logoFile : null,
        imageUrl: base.logoOriginalUrl || base.logoUrl,
        rootCreationId: base.logoRootCreationId,
        creationId: base.logoCreationId,
      });
      if (!cutoutUrl) throw new Error("Background removal returned no image.");

      if (
        cutoutUrlRef.current?.startsWith("blob:") &&
        cutoutUrlRef.current !== cutoutUrl
      ) {
        URL.revokeObjectURL(cutoutUrlRef.current);
      }
      cutoutUrlRef.current = cutoutUrl.startsWith("blob:") ? cutoutUrl : null;
      applyCutoutLogo(cutoutUrl, creationId);
    } catch (err) {
      console.error("[SignPdfSignatureModal] BG remove failed:", err);
      setBgRemoveError(
        err?.message || "Could not remove the background. Please try again."
      );
    } finally {
      setBgRemoveBusy(false);
    }
  };

  const handleBgRemove = () => {
    const base = draftRef.current;
    if (bgRemoveBusy || base?.logoBgRemoved || !base?.logoUrl) return;
    if (!canAccessPaywallFeature("logo_bg_remove")) {
      setPaywallOpen(true);
      return;
    }
    void runBgRemove();
  };

  const handleApply = () => {
    if (!canApply || !draft) return;
    // Hand logo blob ownership to the session — do not revoke on unmount.
    const appliedUrl = draft.logoUrl;
    if (
      logoObjectUrlRef.current &&
      logoObjectUrlRef.current === appliedUrl
    ) {
      logoObjectUrlRef.current = null;
    }
    if (
      cutoutUrlRef.current &&
      cutoutUrlRef.current === appliedUrl
    ) {
      cutoutUrlRef.current = null;
    }
    onApply(draft);
    onClose();
  };

  if (!open || !draft) return null;

  const node = (
    <div className="spsm-root" role="presentation">
      <button
        type="button"
        className="spsm-backdrop"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        className="spsm-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <button
          type="button"
          className="spsm-close"
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>

        <div className="spsm-header">
          <h2 id={titleId} className="spsm-title">
            {copy.title}
          </h2>
          <p className="spsm-subtitle">{copy.subtitle}</p>
        </div>

        <div className="spsm-split">
          <nav className="spsm-rail" aria-label={copy.railLabel}>
            {SIGN_PDF_MODE_OPTIONS.map((option) => {
              const Icon = MODE_ICONS[option.id];
              const selected = mode === option.id;
              const item = (
                <button
                  type="button"
                  className={`spsm-rail__item${selected ? " is-selected" : ""}${
                    !option.available ? " is-soon" : ""
                  }`}
                  aria-current={selected ? "page" : undefined}
                  onClick={() => selectMode(option.id)}
                >
                  <span className="spsm-rail__icon">
                    <Icon />
                  </span>
                  <span className="spsm-rail__text">
                    <span className="spsm-rail__label">{option.label}</span>
                    {!option.available ? (
                      <span className="spsm-rail__soon">Soon</span>
                    ) : null}
                  </span>
                </button>
              );

              if (!option.tip) {
                return <React.Fragment key={option.id}>{item}</React.Fragment>;
              }

              return (
                <Tooltip
                  key={option.id}
                  content={option.tip}
                  position="right"
                  size="wide"
                  block
                >
                  {item}
                </Tooltip>
              );
            })}
          </nav>

          <div className="spsm-editor">
            {mode === SIGN_PDF_MODES.TYPE ? (
              <div className="spsm-editor__body">
                <div className="spsm-fields">
                  <div className="spsm-field">
                    <label className="spsm-label" htmlFor="spsm-text">
                      {copy.nameLabel}
                    </label>
                    <input
                      id="spsm-text"
                      type="text"
                      className="spsm-input"
                      value={draft.text || ""}
                      placeholder={copy.placeholder}
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      onChange={(e) => patch({ text: e.target.value })}
                    />
                  </div>

                  <div className="spsm-field">
                    <span className="spsm-label" id="spsm-font-label">
                      {copy.fontLabel}
                    </span>
                    <div className="spsm-font" ref={fontRef}>
                      <button
                        type="button"
                        className="spsm-font__trigger"
                        aria-labelledby="spsm-font-label"
                        aria-haspopup="listbox"
                        aria-expanded={fontOpen}
                        onClick={() => setFontOpen((v) => !v)}
                      >
                        <span
                          style={{
                            fontFamily: getWatermarkFontStack(font?.id),
                          }}
                        >
                          {font?.label || "Font"}
                        </span>
                      </button>
                      {fontOpen ? (
                        <ul
                          className="spsm-font__menu"
                          role="listbox"
                          aria-label="Font"
                        >
                          {fonts.map((f) => (
                            <li key={f.id}>
                              <button
                                type="button"
                                role="option"
                                aria-selected={f.id === font?.id}
                                className={`spsm-font__item${
                                  f.id === font?.id ? " is-active" : ""
                                }`}
                                style={{ fontFamily: f.stack }}
                                onClick={() => {
                                  patch({ fontId: f.id });
                                  setFontOpen(false);
                                }}
                              >
                                {f.label}
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="spsm-canvas-wrap">
                  <div
                    className={`spsm-preview spsm-preview--type spsm-preview--${canvasTheme.id}`}
                  >
                    <span
                      className="spsm-preview__text"
                      style={{
                        fontFamily: getWatermarkFontStack(font?.id),
                        color: canvasTheme.textColor,
                      }}
                    >
                      {String(draft.text || "").trim() || copy.placeholder}
                    </span>
                  </div>
                  <CanvasThemeSwitcher
                    canvasTheme={canvasTheme}
                    onSelect={selectCanvasTheme}
                  />
                </div>
              </div>
            ) : null}

            {mode === SIGN_PDF_MODES.UPLOAD ? (
              <div className="spsm-editor__body">
                <div className="spsm-canvas-wrap">
                  <div
                    className={`spsm-preview spsm-preview--type spsm-preview--upload spsm-preview--${canvasTheme.id}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (uploadProgress != null || bgRemoveBusy) return;
                      if (!draft.logoUrl) {
                        openSignaturePicker();
                        return;
                      }
                      setImageSelected(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter" && e.key !== " ") return;
                      e.preventDefault();
                      if (uploadProgress != null || bgRemoveBusy) return;
                      if (!draft.logoUrl) openSignaturePicker();
                    }}
                  >
                    {draft.logoUrl && uploadProgress == null ? (
                      <SignPdfUploadMark
                        settings={draft}
                        selected={imageSelected}
                        disabled={bgRemoveBusy}
                        onSelect={() => setImageSelected(true)}
                        onChange={(next) => {
                          setDraft(withSignPdfSingleMark(next));
                          setImageSelected(true);
                        }}
                      />
                    ) : (
                      <div
                        className="spsm-upload-slot"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ToolPrepUploadDropzone
                          kind="logo"
                          label="Upload signature"
                          hint="Supported formats: JPG, PNG, WEBP, GIF"
                          progressLabel="Preparing signature…"
                          progress={uploadProgress}
                          onClick={openSignaturePicker}
                          disabled={bgRemoveBusy}
                          className="spsm-upload-slot__dropzone"
                        />
                      </div>
                    )}
                  </div>

                  {imageSelected &&
                  draft.logoUrl &&
                  uploadProgress == null &&
                  !draft.logoCropActive ? (
                    <div
                      className="spsm-upload-toolbar"
                      role="toolbar"
                      aria-label="Signature image tools"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="spsm-upload-toolbar__btn"
                        disabled={bgRemoveBusy || Boolean(draft.logoBgRemoved)}
                        onClick={handleBgRemove}
                      >
                        {bgRemoveBusy
                          ? "Removing…"
                          : draft.logoBgRemoved
                            ? "BG removed"
                            : "Remove BG"}
                      </button>
                      <button
                        type="button"
                        className="spsm-upload-toolbar__btn"
                        disabled={bgRemoveBusy || Boolean(draft.logoCropActive)}
                        onClick={() => {
                          setDraft(
                            withSignPdfSingleMark(beginSignPdfUploadCrop(draft))
                          );
                          setImageSelected(true);
                        }}
                      >
                        Crop
                      </button>
                      <button
                        type="button"
                        className="spsm-upload-toolbar__btn"
                        disabled={bgRemoveBusy}
                        onClick={openSignaturePicker}
                      >
                        Replace
                      </button>
                      <button
                        type="button"
                        className="spsm-upload-toolbar__btn spsm-upload-toolbar__btn--danger"
                        disabled={bgRemoveBusy}
                        onClick={clearSignatureImage}
                      >
                        Delete
                      </button>
                    </div>
                  ) : null}

                  {bgRemoveError ? (
                    <p className="spsm-error spsm-error--overlay" role="alert">
                      {bgRemoveError}
                    </p>
                  ) : null}

                  <CanvasThemeSwitcher
                    canvasTheme={canvasTheme}
                    onSelect={selectCanvasTheme}
                  />
                </div>
              </div>
            ) : null}

            {mode === SIGN_PDF_MODES.DRAW ? (
              <div className="spsm-editor__body spsm-editor__body--draw">
                <div className="spsm-draw-stage">
                  <SignPdfDrawPad
                    key={`draw-${open ? "open" : "closed"}-${kind}`}
                    penColor={canvasTheme.textColor}
                    themeId={canvasTheme.id}
                    disabled={bgRemoveBusy}
                    onExport={applyDrawnSignature}
                  />
                  <CanvasThemeSwitcher
                    canvasTheme={canvasTheme}
                    onSelect={selectCanvasTheme}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="spsm-footer">
          <button type="button" className="spsm-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="spsm-primary"
            disabled={!canApply}
            onClick={handleApply}
          >
            {copy.apply}
          </button>
        </div>
      </div>
      <ImagePickerModal {...imagePickerProps} />
      <FeaturePaywallModal
        open={paywallOpen}
        featureKey="logo_bg_remove"
        toolKey="sign_pdf"
        onClose={() => setPaywallOpen(false)}
      />
    </div>
  );

  return createPortal(node, document.body);
};

export default SignPdfSignatureModal;
